/**
 * Helpers for RXNT Integration SDK launch from pending worklist / notification rows.
 *
 * Patient context priority (mirrors API RxntLaunchService::resolvePatientData):
 * 1. Snap employee_id (richest demographics from SnapEmployeeModel)
 * 2. rxnt_patient_id → API looks up rxnt_patients + optional employee link
 * 3. gatewayPatient (rx.patient on worklist row) — client-side map if API returns no patientData
 */
import { defaultRxntLaunchReturnUrl } from './rxntLaunchSdk';

function normalizePhone(phone) {
  if (!phone) return '';
  let digits = String(phone).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length > 10) {
    digits = digits.slice(0, 10);
  }
  return digits;
}

function normalizeDob(dob) {
  if (!dob) return '';
  const s = String(dob);
  if (s.includes('T')) return s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
  } catch {
    /* ignore */
  }
  return s.slice(0, 10);
}

/**
 * Map RXNT gateway worklist `patient` to SDK launchData.patientData shape.
 * Used only when GET /launch/context did not include patientData (no Snap employee / thin rxnt row).
 */
export function mapGatewayPatientToSdk(patient, chartNumber = '') {
  if (!patient || typeof patient !== 'object') return null;

  const sexRaw = patient.sex || patient.gender || 'U';
  let sex = String(sexRaw).toUpperCase().charAt(0);
  if (sex !== 'M' && sex !== 'F') sex = 'U';

  const addr = patient.address && typeof patient.address === 'object' ? patient.address : {};
  const phone = patient.phone || patient.contact?.phone || '';
  const email = patient.email || patient.contact?.email || '';
  let firstName = patient.firstName || '';
  let lastName = patient.lastName || '';
  if (!firstName && !lastName && patient.fullName) {
    const parts = String(patient.fullName).trim().split(/\s+/);
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  }

  // Practice chart id only — not RXNT gateway patientId (internal RX id).
  const chart = chartNumber || patient.chartNumber || patient.chartNo || '';

  return {
    firstName,
    lastName,
    dateOfBirth: normalizeDob(patient.dateOfBirth || patient.dob || patient.birthDate),
    sex,
    chartNumber: String(chart),
    phone: normalizePhone(phone),
    email: email || '',
    address: {
      address1: addr.address1 || addr.line1 || patient.address1 || '',
      address2: addr.address2 || addr.line2 || '',
      city: addr.city || patient.city || '',
      state: addr.state || patient.state || '',
      zip: addr.zip || addr.zipCode || patient.zip || '',
    },
  };
}

export function rxntGatewayPatientId(rx) {
  if (!rx) return null;
  if (rx.patientId != null && rx.patientId !== '') return Number(rx.patientId);
  const p = rx.patient;
  if (!p) return null;
  const id = p.patientId ?? p.id ?? p.patient_id;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Return URL after RXNT session — lands on RXNT prescriptions results with patient filter when possible.
 */
export function buildRxntLaunchReturnUrl({ employeeId } = {}) {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  const base = `${origin}/result/rxnt-prescriptions`;
  if (employeeId) {
    const qs = new URLSearchParams();
    qs.set('employee_id', String(employeeId));
    qs.set('tab', 'pending');
    return `${base}?${qs.toString()}`;
  }
  return defaultRxntLaunchReturnUrl();
}

/**
 * Launch params for a pending notification / worklist row.
 */
export function launchParamsFromPendingRow(row) {
  const rx = row?.rx || {};
  const local = row?.local || {};
  const employeeId = local?.employee_id || null;
  const rxntPatientId =
    local?.rxnt_patient_id != null
      ? Number(local.rxnt_patient_id)
      : rxntGatewayPatientId(rx);

  return {
    providerId: row?.provider_id || '',
    rxntDoctorId: row?.rxnt_doctor_id ?? null,
    employeeId,
    rxntPatientId: rxntPatientId != null && rxntPatientId > 0 ? rxntPatientId : null,
    gatewayPatient: rx?.patient || null,
    returnUrl: buildRxntLaunchReturnUrl({ employeeId }),
  };
}

export function pendingRowCanLaunchWithPatient(row) {
  const p = launchParamsFromPendingRow(row);
  const gp = p.gatewayPatient;
  const hasGateway =
    gp &&
    (gp.firstName ||
      gp.lastName ||
      gp.fullName ||
      gp.patientId != null);
  return Boolean(
    p.employeeId || (p.rxntPatientId != null && p.rxntPatientId > 0) || hasGateway
  );
}
